import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

// Enregistrer les tailles de police supportées (en points, cohérent avec ReportLab)
const SizeStyle = Quill.import("attributors/style/size");
SizeStyle.whitelist = ["8pt", "9pt", "10pt", "11pt", "12pt", "14pt", "16pt", "18pt", "20pt", "24pt", "28pt", "32pt"];
Quill.register(SizeStyle, true);

// Polices supportées (celles qui ont un équivalent ReportLab)
const Font = Quill.import("attributors/style/font");
Font.whitelist = ["Arial", "Helvetica", "Times New Roman", "Georgia", "Courier New", "Verdana", "Calibri"];
Quill.register(Font, true);

// Couleur par style inline (plus fiable que la version par classe pour ReportLab)
const ColorStyle = Quill.import("attributors/style/color");
Quill.register(ColorStyle, true);
const BgStyle = Quill.import("attributors/style/background");
Quill.register(BgStyle, true);

const DEFAULT_MODULES = {
  toolbar: {
    container: [
      [{ font: Font.whitelist }],
      [{ size: SizeStyle.whitelist }],
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["blockquote", "link", "clean"],
    ],
  },
  clipboard: { matchVisual: false },
};

const DEFAULT_FORMATS = [
  "font", "size", "header",
  "bold", "italic", "underline", "strike",
  "color", "background",
  "align",
  "list", "bullet", "indent",
  "blockquote", "link",
];

/**
 * Éditeur de texte riche style Word (gère police, taille, couleur, alignement, listes…).
 * `value` : HTML string. `onChange` : (html) => void.
 * `onApi` : callback optionnel pour exposer une API { insertAtCursor(text) }.
 */
export default function RichTextEditor({ value, onChange, placeholder = "", minHeight = 320, onApi }) {
  const quillRef = useRef(null);
  const [customColor, setCustomColor] = useState("#1E293B");
  const [customBg, setCustomBg] = useState("#FEF08A");

  const modules = useMemo(() => DEFAULT_MODULES, []);

  useEffect(() => {
    if (!onApi) return;
    onApi({
      insertAtCursor: (text) => {
        const editor = quillRef.current?.getEditor?.();
        if (!editor) return;
        const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
        editor.insertText(range.index, text, "user");
        editor.setSelection(range.index + text.length, 0);
      },
      focus: () => quillRef.current?.getEditor?.()?.focus(),
    });
  }, [onApi]);

  const applyFormat = useCallback((format, value) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true);
    if (!range || range.length === 0) {
      // Rien de sélectionné → définir la couleur pour la prochaine saisie
      editor.format(format, value, "user");
      editor.focus();
      return;
    }
    editor.formatText(range.index, range.length, format, value, "user");
    editor.focus();
  }, []);

  const clearFormat = useCallback((format) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true);
    if (!range || range.length === 0) {
      editor.format(format, false, "user");
    } else {
      editor.formatText(range.index, range.length, format, false, "user");
    }
    editor.focus();
  }, []);

  return (
    <div className="rte-wrapper" style={{ "--rte-min": `${minHeight}px` }} data-testid="rte-root">
      {/* Barre d'outils complémentaire : sélecteurs de couleur libres (hex) */}
      <div className="flex items-center gap-3 px-2 py-1.5 bg-slate-50 border border-slate-200 border-b-0 rounded-t-md text-[11px] text-slate-600 flex-wrap">
        <label className="inline-flex items-center gap-1.5" title="Couleur du texte (libre)">
          <span>Texte</span>
          <input type="color" data-testid="rte-color-text" value={customColor}
                 onChange={(e)=>{ setCustomColor(e.target.value); applyFormat("color", e.target.value); }}
                 className="h-6 w-9 border border-slate-300 rounded cursor-pointer"/>
          <button type="button" onClick={()=>clearFormat("color")}
                  className="text-[10px] px-1.5 py-0.5 border border-slate-300 rounded hover:bg-slate-100">
            ⨯
          </button>
        </label>
        <span className="h-4 w-px bg-slate-300"/>
        <label className="inline-flex items-center gap-1.5" title="Couleur de surlignage (libre)">
          <span>Surlignage</span>
          <input type="color" data-testid="rte-color-bg" value={customBg}
                 onChange={(e)=>{ setCustomBg(e.target.value); applyFormat("background", e.target.value); }}
                 className="h-6 w-9 border border-slate-300 rounded cursor-pointer"/>
          <button type="button" onClick={()=>clearFormat("background")}
                  className="text-[10px] px-1.5 py-0.5 border border-slate-300 rounded hover:bg-slate-100">
            ⨯
          </button>
        </label>
        <span className="text-[10px] text-slate-400 ml-auto">Sélectionnez du texte puis cliquez sur la couleur</span>
      </div>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        modules={modules}
        formats={DEFAULT_FORMATS}
      />
      <style>{`
        .rte-wrapper .ql-container { min-height: var(--rte-min, 320px); font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; }
        .rte-wrapper .ql-editor { min-height: var(--rte-min, 320px); line-height: 1.55; }
        .rte-wrapper .ql-toolbar.ql-snow, .rte-wrapper .ql-container.ql-snow { border-color: rgb(203 213 225); }
        .rte-wrapper .ql-toolbar.ql-snow { border-top-left-radius: 0; border-top-right-radius: 0; background: rgb(248 250 252); }
        .rte-wrapper .ql-container.ql-snow { border-bottom-left-radius: 6px; border-bottom-right-radius: 6px; }

        /* Nommer les options de taille et police dans la barre d'outils */
        .ql-snow .ql-picker.ql-size .ql-picker-item::before,
        .ql-snow .ql-picker.ql-size .ql-picker-label::before { content: attr(data-value); }
        .ql-snow .ql-picker.ql-size .ql-picker-label:not([data-value])::before { content: "Taille"; }
        .ql-snow .ql-picker.ql-font .ql-picker-item::before,
        .ql-snow .ql-picker.ql-font .ql-picker-label::before { content: attr(data-value); font-family: inherit; }
        .ql-snow .ql-picker.ql-font .ql-picker-label:not([data-value])::before { content: "Police"; }

        /* Faire ressembler chaque option de police à sa vraie typo pour un meilleur confort */
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Arial"]::before { font-family: Arial, sans-serif; }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Helvetica"]::before { font-family: Helvetica, Arial, sans-serif; }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Times New Roman"]::before { font-family: "Times New Roman", Times, serif; }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Georgia"]::before { font-family: Georgia, serif; }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Courier New"]::before { font-family: "Courier New", Courier, monospace; }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Verdana"]::before { font-family: Verdana, Geneva, sans-serif; }
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="Calibri"]::before { font-family: Calibri, sans-serif; }
      `}</style>
    </div>
  );
}
